import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';

import { Subscription, combineLatest, Observable } from 'rxjs';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { AbstractChartOptionsModel } from '../../models/abstract-chart-options.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { ChartGroupInputModel } from '../../models/chart-group-input.model';
import { ChartMetadataModel } from '../../models/chart-metadata.model';
import { DataPointModel } from '../../models/data-point.model';
import { ChartLoadingOptionsEnum } from '../../enums/chart-loading-options.enum';
import { ChartModel } from '../../models/chart.model';
import { ChartComponent } from '../Chart/chart.component';
import { ReportsSeriesDataMergerService } from '../../../Reports/services/reports-series-data-merger.service';


@Component({
    selector: 'agpr-chart-group',
    templateUrl: './chart-group.component.html',
    styleUrls: ['./chart-group.component.scss'],
    standalone: false
})
export class ChartGroupComponent extends ChartComponent implements AfterViewInit, OnDestroy {
    @Input() id: string = null;
    @Input() set charts(chartGroup: ChartGroupInputModel) {
        this.destroyOldCharts();
        this._charts = chartGroup.charts;
        this.options = chartGroup.options;
        this.metadata = chartGroup.metadata;
        this.initializeChartContainer();
        this.initializeCharts();
    }
    @Input() showLoadingUntil: ChartLoadingOptionsEnum = ChartLoadingOptionsEnum.all;
    @Input() subChartsTotalWidthPercentage: number = 90;

    options: AbstractChartOptionsModel;
    metadata: ChartMetadataModel;
    initFrame: number;
    viewInitted: boolean = false;
    loading: boolean = false;
    loadingSubscriber: Subscription;

    protected _charts: ChartModel[];
    protected chartContainer: am4core.Container;
    protected subChartContainer: am4core.Container;
    protected legendContainer: am4charts.Legend;
    protected legendWrapper: HTMLDivElement;

    @ViewChild('chartGroupElement', { static: true }) chartGroupElement: ElementRef;

    constructor(
        public seriesDataMergerService: ReportsSeriesDataMergerService,
        public zone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
    ) {
        super(seriesDataMergerService, zone, changeDetectorRef);
        // TODO: deal with container legend change
    }

    ngAfterViewInit() {
        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            this.viewInitted = true;
            this.initializeChartContainer();
            this.initializeCharts();
            this.initializeLegend();
        });
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.initFrame);
        this.destroyOldCharts();
    }

    /**
     * Removes the old chart from the DOM, then waits 1 frame before calling destroy.
     * This is faster because when amcharts is destroying a chart, it does a lot of DOM manipulation
     * and it is a lot faster if it's not part of the document.
     */
    destroyOldCharts() {
        // Remove watches for previous series
        this.unwatchLoading();
        this._charts?.forEach(chart => chart.destroy());
        this.legendContainer = null;
        this.dataSubscriptions.forEach(x => x.unsubscribe());
    }

    /**
     * Initialization for the root container for metadata, charts, and legend
     */
    public initializeChartContainer() {
        this.chartContainer = am4core.create(this.chartGroupElement.nativeElement, am4core.Container);
        this.chartContainer.layout = 'vertical';
        this.chartContainer.width = am4core.percent(100);
        this.chartContainer.height = am4core.percent(100);
        this.chartContainer.svgContainer.htmlElement.style.height =  this.options.styles.chartHeight + 'px';

        const titleLabel = this.chartContainer.createChild(am4core.Label);
        titleLabel.fontSize = 18;
        titleLabel.fontWeight = '700';
        titleLabel.fill = this.options.styles.titleLabelColor;
        titleLabel.align = 'left';
        titleLabel.truncate = true;
        titleLabel.width = am4core.percent(100);
        titleLabel.marginBottom = 0;
        titleLabel.paddingBottom = 0;
        titleLabel.html = `<h4 class="agpr-chartGroup-header">${this.metadata.titleText}</h4>`;

        if (this.options.styles?.customPadding) {
            this.chartContainer.padding(
                this.options.styles.customPadding.top,
                this.options.styles.customPadding.right,
                this.options.styles.customPadding.bottom,
                this.options.styles.customPadding.left
            );
        } else {
            if (!this.options.noPadding) {
                this.chartContainer.padding(20, 20, 20, 20);
            } else {
                this.chartContainer.padding(0, 0, 0, 0);
            }
        }

        this.subChartContainer = this.chartContainer.createChild(am4core.Container);
        this.subChartContainer.layout = 'horizontal';
        this.subChartContainer.align = 'center';
        this.subChartContainer.width = am4core.percent(this.subChartsTotalWidthPercentage);
        this.subChartContainer.height = am4core.percent(100);
    }

    public initializeCharts(): void {
        if (!this.viewInitted) {
            return;
        }

        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            if (this._charts.length) {
                this.zone.runOutsideAngular(() => {
                    this._charts.forEach(chart => {
                        chart.setZone(this.zone);
                        chart.initChart(this.subChartContainer, chart.chartType);
                        this.watchData(chart);
                        this.watchLoading();
                    });
                });
            }
        });
    }

    public initializeLegend(): void {
        if (this.legendContainer) {
            return;
        }

        this.legendContainer = this.chartContainer.createChild(am4charts.Legend);
        this.legendContainer.width = am4core.percent(100);
        this.legendContainer.labels.template.align = 'center';
        this.legendContainer.labels.template.fill = am4core.color(chartColors.agprText);
        this.legendContainer.itemContainers.template.paddingTop = 5;
        this.legendContainer.itemContainers.template.paddingBottom = 0;
        this.legendContainer.itemContainers.template.paddingLeft = this.options.styles.legendItemHorizontalPadding;
        this.legendContainer.itemContainers.template.paddingRight = this.options.styles.legendItemHorizontalPadding;
        this.legendContainer.itemContainers.template.clickable = false;
        this.legendContainer.itemContainers.template.focusable = false;
        this.legendContainer.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
        this.legendContainer.itemContainers.template.maxWidth = 200;

        this.legendContainer.itemContainers.template.tooltip = new am4core.Tooltip();
        this.legendContainer.itemContainers.template.tooltipHTML =
            `<div style='line-height: 1.5; max-width: 350px; overflow-wrap: break-word;'>${this.legendContainer.labels.template.text}</div>`;
        this.legendContainer.itemContainers.template.tooltip.getFillFromObject = false;
        this.legendContainer.itemContainers.template.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        this.legendContainer.itemContainers.template.tooltip.background.fillOpacity = 1;
        this.legendContainer.itemContainers.template.tooltip.label.fill = am4core.color(chartColors.agprText);
        this.legendContainer.itemContainers.template.tooltip.background.pointerLength = 0;
        this.legendContainer.itemContainers.template.tooltip.dy = -15;
        this.legendContainer.itemContainers.template.tooltip.dx = 15;
        this.legendContainer.itemContainers.template.tooltip.label.padding(7, 7, 7, 7);
        this.legendContainer.itemContainers.template.tooltip.boundingContainer = this.chartContainer;
        this.legendContainer.labels.template.truncate = true;
        this.legendContainer.itemContainers.template.tooltip.maxWidth = 350;
        this.legendContainer.itemContainers.template.tooltip.label.wrap = true;

        // To show tooltips only when there is an ellipsis
        this.legendContainer.itemContainers.template.adapter.add('tooltipHTML', (text = this.legendContainer.labels.template.text, target) => {

            if (!target.dataItem.dataContext['legendDataItem'].label.isOversized) {
                return '';
            }

            return text;
        });

        const boxShadow = this.legendContainer.itemContainers.template.tooltip.background.filters.push(new am4core.DropShadowFilter());
        boxShadow.blur = 3;
        boxShadow.dy = 2;
        boxShadow.dx = 0;
        boxShadow.color = am4core.color(chartColors.agprText);

    }

    // the legend might change on a redraw, this function will allow that
    public setLegendData(): void {
        this.legendContainer.data = this.options.customLegendItems;
    }

    /**
     * Start watching the loading state of series data
     */
    public watchLoading(): void {
        if (!this._charts || !this._charts.some(chart => !!chart.series)) {
            this.loading = false;
            return;
        }

        if (this.loading || this.loadingSubscriber) {
            return;
        }

        const seriesProviders: Observable<DataPointModel[]>[] = [];
        this._charts.forEach(chart => {
            if (chart.series) {
                seriesProviders.push(...chart.series?.map(series => series.dataProvider));
            }
        });

        this.loading = true;
        if (this.showLoadingUntil === ChartLoadingOptionsEnum.all) {
            this.loadingSubscriber = combineLatest(seriesProviders).subscribe({
                next: (data) => {
                    if (data.filter(x => x !== null).length === data.length) {
                        requestAnimationFrame(() => {
                            this.loading = false;
                            this.initializeLegend();
                            this.setLegendData();
                            this.loadingSubscriber = null;
                        });
                    } else {
                        this.loading = true;
                    }
                },
                error: () => this.loading = false
            });
        } else if (this.showLoadingUntil === ChartLoadingOptionsEnum.any) {
            this.loading = true;
            this.loadingSubscriber = combineLatest(seriesProviders).subscribe({
                next: (data) => {
                    this.loading = true;
                    if (data.filter(x => !!x).length) {
                        this.loading = false;
                        this.initializeLegend();
                        this.setLegendData();
                        this.loadingSubscriber = null;
                    }
                },
                error: () => this.loading = false
            });
        } else {
            this.loading = false;
            this.initializeLegend();
            this.setLegendData();
            this.loadingSubscriber = null;
        }
    }

    /**
     * Stops watching the loading state of series data
     */
    public unwatchLoading(): void {
        this.loading = false;
        if (this.loadingSubscriber) {
            this.loadingSubscriber.unsubscribe();
            this.loadingSubscriber = null;
        }
    }
}
